import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useContext, useCallback } from 'react';
import type { Layer } from './types';
import { ArrowBidirectionalUpDown16Regular, Delete16Regular } from '@fluentui/react-icons';
import { toast } from 'sonner';
import { Checkbox } from '../ui/Checkbox';
import { Button } from '../ui/Button';
import ConfirmDeleteDialog from '../ui/ConfirmDeleteDialog';
import CompositionLayerDialog from './CompositionLayerDialog';
import { useLayerOperations } from './LayerOperationsContext';
import { Link } from 'react-router-dom';

const MEMBRANE_DISPLAY_THICKNESS = 5; // Fixed display thickness (mm) for membrane layers

const columnHelper = createColumnHelper<Layer>();

// This will be set from CompositionLayersTable
export let DragContext: React.Context<{ attributes?: any; listeners?: any }> | null = null;

export const setDragContext = (context: React.Context<{ attributes?: any; listeners?: any }>) => {
  DragContext = context;
};

export const RowDragHandleCell = () => {
  const dragContext = DragContext ? useContext(DragContext) : {};
  const { attributes, listeners } = dragContext || {};

  return (
    <div
      {...attributes}
      {...listeners}
      data-testid="composition-layer-drag-handle"
      className="cursor-move"
    >
      <ArrowBidirectionalUpDown16Regular />
    </div>
  );
};

export function useColumns() {
  const { entityId, layers, deleteLayer, isDeleting, onSuccess } = useLayerOperations();

  const handleDelete = useCallback(
    (layerId: string) => {
      if (!entityId) return;

      deleteLayer(
        {
          instanceTypeId: entityId,
          layerId,
        },
        {
          onSuccess: () => {
            toast.success('Vrstva byla smazána');
            onSuccess?.();
          },
          onError: (error) => {
            console.error(error);
          },
        }
      );
    },
    [entityId, onSuccess, deleteLayer]
  );

  return useMemo<ColumnDef<Layer>[]>(
    () => [
      {
        id: 'drag-handle',
        header: '',
        cell: () => <RowDragHandleCell />,
        size: 40,
        enableSorting: false,
      },
      columnHelper.accessor('order', {
        header: 'Pořadí',
        cell: (info) => <span>{info.getValue()}</span>,
        size: 40,
        enableSorting: false,
      }),
      columnHelper.accessor('function', {
        header: 'Funkce',
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor('solution.title', {
        id: 'solution',
        header: 'Řešení',
        cell: (info) => {
          const solution = info.row.original.solution;
          return solution ? (
            <Link
              data-testid={`composition-layer-solution-link-${info.row.original.id}`}
              to={`/app/data/reseni/reseni/${solution.id_root}`}
            >
              {info.row.original.solution?.title}
            </Link>
          ) : null;
        },
      }),
      columnHelper.accessor('thickness', {
        header: 'Tloušťka',
        cell: (info) => {
          const isMembrane = info.row.original.function === 'membrane_layer';
          return <span>{isMembrane ? MEMBRANE_DISPLAY_THICKNESS : info.getValue()} mm</span>;
        },
      }),
      columnHelper.accessor('is_wrap', {
        header: 'Zalomení',
        cell: (info) => <Checkbox checked={info.getValue()} disabled />,
        size: 80,
      }),
      columnHelper.accessor('is_structural', {
        header: 'Konstrukční',
        cell: (info) => <Checkbox checked={info.getValue()} disabled />,
        size: 80,
      }),
      columnHelper.accessor('is_variable', {
        header: 'Proměnná',
        cell: (info) => <Checkbox className="text-white" checked={info.getValue()} disabled />,
        size: 80,
      }),
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end items-center gap-2">
            {entityId && (
              <CompositionLayerDialog variant="edit" layer={row.original} layers={layers} />
            )}

            {entityId && (
              <ConfirmDeleteDialog
                title="Opravdu chcete odstranit vrstvu?"
                onConfirm={() => handleDelete(row.original.id)}
                disabled={isDeleting}
              >
                <Button
                  data-testid={`composition-layer-delete-btn-${row.original.id}`}
                  variant="secondary"
                  className="size-7"
                  size="icon"
                  disabled={isDeleting}
                >
                  <Delete16Regular />
                </Button>
              </ConfirmDeleteDialog>
            )}
          </div>
        ),
        size: 40,
        enableSorting: false,
      },
    ],
    [entityId, onSuccess, handleDelete, isDeleting, layers]
  );
}
